import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { ResponsiveFunnel } from '@nivo/funnel';
import { ResponsiveSankey } from '@nivo/sankey';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { advancedReportsAPI } from '../../services/api';

interface JourneyStage {
  id: string;
  label: string;
  value: number;
  percentage: number;
  dropOffRate: number;
  avgTimeInStage: number; // minutes
  [key: string]: string | number; // Index signature for Nivo compatibility
}

interface CustomerJourneyData {
  funnel_data: JourneyStage[];
  stage_metrics: {
    [key: string]: {
      total_entered: number;
      total_completed: number;
      avg_time_spent: number;
      conversion_rate: number;
    };
  };
  average_journey_time: number;
  completion_rate: number;
  drop_off_points: Array<{
    stage: string;
    drop_off_count: number;
    drop_off_percentage: number;
  }>;
  channel_flow?: any; // For Sankey diagram
  time_heatmap?: any; // For heatmap
}

interface CustomerJourneyAnalyticsProps {
  startDate: string;
  endDate: string;
  channelId?: string;
}

const CustomerJourneyAnalytics: React.FC<CustomerJourneyAnalyticsProps> = ({
  startDate,
  endDate,
  channelId
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CustomerJourneyData | null>(null);

  useEffect(() => {
    fetchJourneyData();
  }, [startDate, endDate, channelId]);

  const fetchJourneyData = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call when backend is ready
      // const response = await advancedReportsAPI.getCustomerJourney({
      //   start_date: startDate,
      //   end_date: endDate,
      //   channel_id: channelId
      // });

      // Mock data for now - matching the structure from ADVANCED_ANALYTICS_SCENARIOS.md
      const mockData: CustomerJourneyData = {
        funnel_data: [
          {
            id: 'initial_contact',
            label: 'Initial Contact',
            value: 1000,
            percentage: 100,
            dropOffRate: 0,
            avgTimeInStage: 2
          },
          {
            id: 'first_response',
            label: 'First Response',
            value: 950,
            percentage: 95,
            dropOffRate: 5,
            avgTimeInStage: 15
          },
          {
            id: 'in_progress',
            label: 'In Progress',
            value: 850,
            percentage: 85,
            dropOffRate: 10.5,
            avgTimeInStage: 120
          },
          {
            id: 'pending_customer',
            label: 'Pending Customer',
            value: 820,
            percentage: 82,
            dropOffRate: 3.5,
            avgTimeInStage: 240
          },
          {
            id: 'resolved',
            label: 'Resolved',
            value: 800,
            percentage: 80,
            dropOffRate: 2.4,
            avgTimeInStage: 30
          },
          {
            id: 'csat_submitted',
            label: 'CSAT Submitted',
            value: 560,
            percentage: 56,
            dropOffRate: 30,
            avgTimeInStage: 5
          }
        ],
        stage_metrics: {
          initial_contact: {
            total_entered: 1000,
            total_completed: 950,
            avg_time_spent: 2,
            conversion_rate: 95.0
          },
          first_response: {
            total_entered: 950,
            total_completed: 850,
            avg_time_spent: 15,
            conversion_rate: 89.5
          },
          // ... other stages
        },
        average_journey_time: 412, // minutes
        completion_rate: 56.0,
        drop_off_points: [
          { stage: 'CSAT Submission', drop_off_count: 240, drop_off_percentage: 30.0 },
          { stage: 'In Progress', drop_off_count: 100, drop_off_percentage: 10.5 },
          { stage: 'First Response', drop_off_count: 50, drop_off_percentage: 5.0 }
        ]
      };

      setData(mockData);
    } catch (err: any) {
      console.error('Failed to fetch customer journey data:', err);
      setError(err.message || 'Failed to load customer journey analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info">No customer journey data available for the selected period.</Alert>
    );
  }

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <Box>
      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Completion Rate
              </Typography>
              <Typography variant="h4" color="primary">
                {data.completion_rate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Customers completing full journey
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Avg Journey Time
              </Typography>
              <Typography variant="h4" color="primary">
                {formatTime(data.average_journey_time)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                From initial contact to resolution
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Total Conversations
              </Typography>
              <Typography variant="h4" color="primary">
                {data.funnel_data[0]?.value.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Started in this period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Completed Journeys
              </Typography>
              <Typography variant="h4" color="primary">
                {data.funnel_data[data.funnel_data.length - 1]?.value.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                With CSAT feedback
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Funnel Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Journey Funnel
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Track customer progression through support stages and identify drop-off points
            </Typography>
            <Box height={500}>
              <ResponsiveFunnel
                data={data.funnel_data}
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                valueFormat=">-.0f"
                colors={{ scheme: 'spectral' }}
                borderWidth={20}
                borderColor={{
                  from: 'color',
                  modifiers: [['darker', 0.3]]
                }}
                borderOpacity={0.7}
                labelColor={{
                  from: 'color',
                  modifiers: [['darker', 3]]
                }}
                beforeSeparatorLength={100}
                beforeSeparatorOffset={20}
                afterSeparatorLength={100}
                afterSeparatorOffset={20}
                currentPartSizeExtension={10}
                currentBorderWidth={40}
                motionConfig="slow"
                tooltip={({ part }) => {
                  const percentage = typeof part.data.percentage === 'number' ? part.data.percentage : parseFloat(String(part.data.percentage));
                  const dropOffRate = typeof part.data.dropOffRate === 'number' ? part.data.dropOffRate : parseFloat(String(part.data.dropOffRate));
                  const avgTime = typeof part.data.avgTimeInStage === 'number' ? part.data.avgTimeInStage : parseFloat(String(part.data.avgTimeInStage));

                  return (
                    <Box
                      sx={{
                        background: '#fff',
                        padding: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {part.data.label}
                      </Typography>
                      <Typography variant="body2">
                        Conversations: {typeof part.data.value === 'number' ? part.data.value.toLocaleString() : part.data.value}
                      </Typography>
                      <Typography variant="body2">
                        Percentage: {percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="error">
                        Drop-off: {dropOffRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Avg Time: {formatTime(avgTime)}
                      </Typography>
                    </Box>
                  );
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Drop-off Analysis */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Drop-off Points
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Stages where customers abandon most frequently
            </Typography>
            <Box>
              {data.drop_off_points.map((dropOff, index) => (
                <Box key={index} mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" fontWeight="medium">
                      {dropOff.stage}
                    </Typography>
                    <Chip
                      label={`${dropOff.drop_off_percentage.toFixed(1)}%`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        flexGrow: 1,
                        height: 8,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${dropOff.drop_off_percentage}%`,
                          height: '100%',
                          bgcolor: '#f44336',
                          transition: 'width 1s ease-in-out'
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {dropOff.drop_off_count}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Stage Details */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Stage Performance
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Stage</TableCell>
                    <TableCell align="right">Conversion</TableCell>
                    <TableCell align="right">Avg Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.funnel_data.map((stage) => (
                    <TableRow key={stage.id}>
                      <TableCell>
                        <Typography variant="caption">{stage.label}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${stage.percentage.toFixed(0)}%`}
                          size="small"
                          color={stage.percentage >= 80 ? 'success' : stage.percentage >= 60 ? 'warning' : 'error'}
                          sx={{ minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="textSecondary">
                          {formatTime(stage.avgTimeInStage)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Business Insights */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom>
          💡 Key Insights & Recommendations
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle2" color="error" gutterBottom>
                ⚠️ Critical Drop-off
              </Typography>
              <Typography variant="body2" paragraph>
                30% of customers don't submit CSAT after resolution. Consider:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><Typography variant="caption">Automated CSAT reminder emails</Typography></li>
                <li><Typography variant="caption">In-app CSAT prompts</Typography></li>
                <li><Typography variant="caption">Simplify CSAT submission process</Typography></li>
              </ul>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                ✅ High Performance
              </Typography>
              <Typography variant="body2" paragraph>
                95% first response rate indicates excellent agent availability
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><Typography variant="caption">Maintain current staffing levels</Typography></li>
                <li><Typography variant="caption">Share best practices across teams</Typography></li>
              </ul>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CustomerJourneyAnalytics;
